import { animate, style, transition, trigger } from "@angular/animations";
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";

import { Subscription } from "rxjs";

import { LINK } from "@hermes/aphrodite/dynamic-box";

import { Asset } from "@hermes/api-model-asset";
import { BoxType, Product } from "@hermes/api-model-product";
import { Context } from "@hermes/app-core";
import { ProductPageFacade } from "@hermes/states/product-page";
import { DOWN, UP } from "@hermes/utils-generic/constants";

export interface AssetData {
    index: number;
    type: string;
}

@Component({
    selector: "h-product-image-gallery",
    templateUrl: "./product-image-gallery.component.html",
    styleUrls: ["./product-image-gallery.component.scss"],
    animations: [
        trigger("fadingAnimation", [
            transition(":enter", [
                style({ opacity: 0 }),
                animate("0.75s ease-in-out", style({ opacity: 1 })),
            ]),
        ]),
        trigger("noopAnimation", [transition(":enter", [])]),
    ],
})
export class ProductImageGalleryComponent implements OnInit, OnDestroy {
    /**
     * Selected image index.
     */
    @Input()
    public currentIndex: number;

    /**
     * Notify parent of selected image index change.
     */
    @Output()
    public currentIndexChange: EventEmitter<[AssetData, boolean]> =
        new EventEmitter<[AssetData, boolean]>();

    /**
     * prefix id of tab
     */
    @Input()
    public prefixTab: string = "tab";

    /**
     * Size of the square image
     */
    @Input()
    public thumbnailSize: number = 130;

    /**
     * Is perso product
     */
    @Input()
    public isPersoProduct: boolean = false;

    public listDirections: any[];

    /**
     * the rest of the images that are not displayed by default
     */
    public remainingImages: number;
    public lastIndex: number;

    private inputMaxVisibleImages: number | undefined = undefined;
    private inputProduct: Product | undefined = undefined;
    private subscription: Subscription = new Subscription();

    constructor(
        private readonly context: Context,
        private productPageFacade: ProductPageFacade,
    ) {}

    @Input()
    public set product(product: Product) {
        this.inputProduct = product;
        Object.assign(
            this,
            this.initOrUpdateImageGalleryInfos(
                this.inputProduct,
                this.inputMaxVisibleImages,
            ),
        );
    }
    public get product() {
        return this.inputProduct;
    }

    /**
     * The maximum number of thumbnails images displayed
     */
    @Input()
    public set maxVisibleImages(maxVisibleImages: number) {
        this.inputMaxVisibleImages = maxVisibleImages;
        Object.assign(
            this,
            this.initOrUpdateImageGalleryInfos(
                this.inputProduct,
                this.inputMaxVisibleImages,
            ),
        );
    }
    public get maxVisibleImages() {
        return this.inputMaxVisibleImages;
    }

    public ngOnInit(): void {
        this.subscription.add(
            this.productPageFacade.variantSelectedBoxType$.subscribe(
                (variantSelectedBoxType: BoxType) => {
                    if (variantSelectedBoxType === LINK) {
                        this.switchImage(0, false);
                    }
                    this.productPageFacade.resetVariantSelectedBoxType();
                },
            ),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public switchImage(index: number, sendEventAnalytic: boolean = true): void {
        this.currentIndexChange.emit([
            {
                index,
                type: this.product.assets[index].type,
            },
            sendEventAnalytic,
        ]);
    }

    /**
     * manage navigation between images using the keyboard
     *
     * @param event  keyboard event
     * @param index  current image index
     */
    public handleThumbnailSelectedWithKey(
        event: KeyboardEvent,
        index: number,
    ): void {
        switch (event.key) {
            case DOWN: {
                if (index === this.lastIndex) {
                    this.focusToTab(0);
                    this.switchImage(0);
                } else {
                    this.focusToTab(index + 1);
                    this.switchImage(index + 1);
                }
                break;
            }
            case UP: {
                if (index === 0) {
                    this.focusToTab(this.lastIndex);
                    this.switchImage(this.lastIndex);
                } else {
                    this.focusToTab(index - 1);
                    this.switchImage(index - 1);
                }
                break;
            }
            default:
        }
    }
    /**
     *  set focus to tab
     *
     * @param index current image index
     */
    public focusToTab(index: number): void {
        if (this.context.isInBrowserMode()) {
            const element = document.getElementById(this.prefixTab + index);
            element.focus();
            if (index === 0) {
                window.scrollTo(0, 0);
            }
        }
    }

    /**
     * generate aria-controls dynamicly with index
     *
     * @param index current image index
     */
    public prefixerAriaControls(index: number): string {
        return `tabpanel${index}`;
    }

    public trackByAsset = (index: number, item: Asset): string | number => {
        if (this.isPersoProduct) {
            return index;
        }
        return `${index}_${item.url}`;
    };

    private initOrUpdateImageGalleryInfos(
        product?: Product,
        maxVisibleImages?: number,
    ): { remainingImages?: number; lastIndex?: number } {
        let remainingImages: number;
        let lastIndex: number;
        if (product && maxVisibleImages !== undefined) {
            remainingImages =
                product?.assets?.length > maxVisibleImages
                    ? product.assets.length - maxVisibleImages
                    : 0;
            lastIndex =
                product?.assets?.length <= maxVisibleImages ||
                remainingImages === 1
                    ? product.assets.length - 1
                    : maxVisibleImages - 1;
        }
        return { remainingImages, lastIndex };
    }
}
